import { useFormikContext } from "formik";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { connectSocket } from "../../../../shared/sockets/socket";
import DOMPurify from "dompurify";
import { marked } from "marked";
import StarterKit from "@tiptap/starter-kit";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import { useEditor } from "@tiptap/react";
import { dispatch } from "../../../../redux/store";
import { setStreaming } from "../../../../redux/slices/authSlice";

const WebSocketContext = createContext();

export const WebSocketProvider = ({ editor, children }) => {
  const { t } = useTranslation();
  const userInfo = useSelector((state) => state?.auth.userInfo);
  const [ws, setWs] = useState(null);

  const formikProps = useFormikContext();

  const editorRef = useRef(null);
  const [openOutlineRewrite, setOpenOutlineRewrite] = useState(false);

  const introHeading = useRef("");
  const [openIntroductionModal, setOpenIntroductionModal] = useState(false);
  const [openIntroductionRewrite, setOpenIntroductionRewrite] = useState(false);

  const [partData, setPartData] = useState(null);
  const [openPartModal, setOpenPartModal] = useState(false);
  const [openPartRewrite, setOpenPartRewrite] = useState(false);

  const [customData, setCustomData] = useState(null);
  const [openCustomModal, setOpenCustomModal] = useState(false);

  const concluHeading = useRef("");
  const [openConclusionModal, setOpenConclusionModal] = useState(false);
  const [openConclusionRewrite, setOpenConclusionRewrite] = useState(false);
  const [analysisState, setAnalysisState] = useState();
  const bufferRef = useRef(""); // Buffer to accumulate chunks
  const counter = useRef(0);

  const tempEditor = useEditor({
    content: "",
    extensions: [
      StarterKit,
      Table.configure({
        resizable: true,
        allowTableNodeSelection: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
  });

  // Add a hook to remove all potential html attributes from rendered elements by gpt to avoid bugs
  // DOMPurify.addHook('uponSanitizeElement', (node) => {
  //   if (node.tagName === 'A') return; // Keep attributes for links
  //   [...node.attributes].forEach((attr) => {
  //     if (!['href', 'src', 'alt', 'title'].includes(attr.name)) {
  //       node.removeAttribute(attr.name);
  //     }
  //   });
  // });

  const validateContent = (content) => {
    try {
      tempEditor.commands.clearContent();
      tempEditor.commands.insertContent(content);
      return true;
    } catch (error) {
      console.error("Invalid content:", error);
      return false;
    }
  };

  const cleanGeneratedContent = (content) => {
    // Supprimer un début partiel ou complet de ```html
    content = content.replace(/^`{0,3}h?t?m?l?/i, "");
    // Supprimer une fin partielle ou complète de ```
    content = content.replace(/`{1,3}$/, "");

    const cleanedContent = content.replace(/>[\s\n\r]+</g, "><");
    const cleanedContent2 = cleanedContent.replace(
      /\s*(N|No|Nom|Nomb|Nombr|Nombre\s*d?e?\s*m?o?t?s?\s*:?\s*\d*)\s*$/,
      ""
    );

    let sanitizedHtml = DOMPurify.sanitize(cleanedContent2, {
      USE_PROFILES: { html: true },
    });

    // Transform eventual markdown
    sanitizedHtml = marked(sanitizedHtml);

    // Handle tables with empty content
    sanitizedHtml = sanitizedHtml.replace(
      /<table>\s*<\/table>/g,
      "<table><thead><tr><th> </th></tr></thead><tbody><tr><td> </td></tr></tbody></table>"
    );
    sanitizedHtml = sanitizedHtml.replace(
      /<thead>\s*<\/thead>/g,
      "<thead><tr><th> </th></tr></thead>"
    );
    sanitizedHtml = sanitizedHtml.replace(
      /<tbody>\s*<\/tbody>/g,
      "<tbody><tr><td> </td></tr></tbody>"
    );
    sanitizedHtml = sanitizedHtml.replace(
      /<tr>\s*<\/tr>/g,
      (match, offset, string) => {
        const precedingPart = string.slice(0, offset).trimEnd();
        const isFirstTr =
          precedingPart.endsWith("<thead>") ||
          (precedingPart.endsWith("<tbody>") && !/<thead>/.test(precedingPart));
        return isFirstTr ? "<tr><th> </th></tr>" : "<tr><td> </td></tr>";
      }
    );
    sanitizedHtml = sanitizedHtml.replace(/<(td|th)>\s*<\/\1>/g, "<$1> </$1>");

    // Handle spaces and all that
    sanitizedHtml = sanitizedHtml.replace(/\n\s{2,}/g, "");
    sanitizedHtml = sanitizedHtml.replace(/>[\n]+</g, "><");

    return sanitizedHtml;
  };

  useEffect(() => {
    try {
      if (userInfo && !ws) {
        const socket = connectSocket(userInfo.unique_id, formikProps.values.id); // Connect to WebSocket
        setWs(socket);

        socket.onmessage = (e) => {
          const data = JSON.parse(e.data);
          dispatch(
            setStreaming({
              isStreaming: true,
            })
          );
          if (data.type === "analysis") {
            setAnalysisState(t(data?.message));
          } else {
            bufferRef.current = bufferRef.current + data.data;
            counter.current = counter.current + 1;

            try {
              if (validateContent(bufferRef.current)) {
                if (data.contentType === "outline") {
                  if (data.isRewrite) {
                    setOpenOutlineRewrite((value) => true);
                    formikProps.setFieldValue(
                      "outline_rewrite",
                      cleanGeneratedContent(bufferRef.current)
                    );
                  } else {
                    // formikProps.setFieldValue('outline', bufferRef.current);
                    editorRef.current.commands.setContent(
                      cleanGeneratedContent(bufferRef.current)
                    );
                  }
                } else if (data.contentType === "introduction") {
                  if (data.isRewrite) {
                    setOpenIntroductionRewrite((value) => true);
                    formikProps.setFieldValue(
                      "introduction_rewrite",
                      cleanGeneratedContent(bufferRef.current)
                    );
                  } else {
                    setOpenIntroductionModal((value) => false);
                    const zone = document.getElementById("introzone");
                    if (zone) {
                      zone.innerHTML = cleanGeneratedContent(
                        introHeading.current + bufferRef.current
                      );
                    }
                  }
                } else if (data.contentType === "part") {
                  if (data.isRewrite) {
                    setOpenPartRewrite((value) => true);
                    setPartData((currentPartData) => {
                      formikProps.setFieldValue("content_rewrite", {
                        ...formikProps.values.content_rewrite,
                        [currentPartData?.title]: cleanGeneratedContent(
                          bufferRef.current
                        ),
                      });
                      return currentPartData;
                    });
                  } else {
                    setOpenPartModal((value) => false);
                    if (cleanGeneratedContent(bufferRef?.current).length) {
                      setPartData((currentPartData) => {
                        try {
                          editor
                            .chain()
                            .focus()
                            .deleteRange({
                              from: currentPartData.startOfPart,
                              to: currentPartData.endOfPart,
                            })
                            .insertContentAt(
                              currentPartData.startOfPart,
                              cleanGeneratedContent(bufferRef.current)
                            )
                            .run();
                        } catch (error) {
                          console.log(error);
                        }

                        const updatedPartData = {
                          ...currentPartData,
                          startOfPart: currentPartData.startOfPart,
                          endOfPart: editor.state.selection.$from.end(2),
                        };

                        return updatedPartData;
                      });
                    }
                  }
                } else if (data.contentType === "conclusion") {
                  if (data.isRewrite) {
                    setOpenConclusionRewrite((value) => true);
                    formikProps.setFieldValue(
                      "conclusion_rewrite",
                      cleanGeneratedContent(bufferRef.current)
                    );
                  } else {
                    setOpenConclusionModal((value) => false);
                    const zone = document.getElementById("conclusionzone");
                    if (zone) {
                      zone.innerHTML = cleanGeneratedContent(
                        concluHeading.current + bufferRef.current
                      );
                    }
                  }
                } else if (data.contentType === "custom") {
                  setOpenCustomModal((value) => false);
                  if (cleanGeneratedContent(bufferRef?.current).length) {
                    setCustomData((currentCustomData) => {
                      try {
                        editor
                          .chain()
                          .focus()
                          .deleteRange({
                            from: currentCustomData.startOfCustom,
                            to: currentCustomData.endOfCustom,
                          })
                          .insertContentAt(
                            currentCustomData.startOfCustom,
                            cleanGeneratedContent(bufferRef.current)
                          )
                          .run();
                      } catch (error) {
                        console.log(error);
                      }

                      const updatedCustomData = {
                        ...currentCustomData,
                        startOfPart: currentCustomData.startOfCustom,
                        endOfCustom: editor.state.selection.$from.end(2),
                      };
                      return updatedCustomData;
                    });
                  }
                }
              }
            } catch (error) {
              console.log(error?.data?.detail);
              console.log(`ERROR RENDERING ${data?.contentType}`);
              console.log(bufferRef.current);
            }
          }
        };
      }
    } catch (error) {
      console.log("ERROR", error);
      console.log(cleanGeneratedContent(bufferRef.current));
    }
  }, [userInfo, ws]);

  return (
    <WebSocketContext.Provider
      value={{
        analysisState,
        editorRef,
        openOutlineRewrite,
        setOpenOutlineRewrite,
        openIntroductionModal,
        setOpenIntroductionModal,
        openIntroductionRewrite,
        setOpenIntroductionRewrite,
        partData,
        setPartData,
        openPartModal,
        setOpenPartModal,
        openPartRewrite,
        setOpenPartRewrite,
        customData,
        setCustomData,
        openCustomModal,
        setOpenCustomModal,
        openConclusionModal,
        setOpenConclusionModal,
        openConclusionRewrite,
        setOpenConclusionRewrite,
        introHeading,
        concluHeading,
        bufferRef,
        cleanGeneratedContent,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

// Custom hook for accessing WebSocket context
export const useWebSocket = () => useContext(WebSocketContext);
